import { interval, Observable, of, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs';

import { TimesheetTypeEnum } from '@shared-enums/cats.enum';
import { SapBooleanEnum } from '@shared-enums/sap.enum';
import { TimesheetStatusEnum } from '@shared-enums/time.enum';
import { syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { safeCompare, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import { IExtDocument } from '@shared-interfaces/documents';

import { ITableConfigEntity } from '../interfaces/internal-interfaces';
import { DatahubService } from '../services/datahub.service';
import { ContractHandler } from './contracts/contract-handler';

// export class ExtDocument implements IExtDocument - TODO: fix the Autocreate type and we can use this instead
export class ExtDocument {
  /* eslint-disable @typescript-eslint/naming-convention -- These are names from SAP, not our code, so it won't comply with our naming conventions */
  public Dateentered: string;
  public Documentno: string;
  public Pernr: string;
  public Timeentered: string;
  public Salesdoc: string;
  public Wbs: string;
  public CondGroup: string;
  public CustGroup: string;
  public Costcenter1: string;
  public Costcenter2: string;
  public Genericfld01: string;
  public Genericfld02: string;
  public Genericfld03: string;
  public Genericfld04: string;
  public Genericfld05: string;
  public Genericfld06: string;
  public Genericfld07: string;
  public Genericfld08: string;
  public Genericfld09: string;
  public Genericfld10: string;
  public Type: TimesheetTypeEnum;
  public CustomerDescription: string; //note that this is different than the `customerDescription` property of the same name but different casing!
  public ContractDescription: string;
  public Description: string;
  public Status: TimesheetStatusEnum;
  public Autocreate: boolean | ''; //TODO: a boolean or a string with an empty value is a very strange type - Look into the usages of this!
  public Owner: string;
  public Link: string;
  public Embed: string;
  public Version: number;
  public Printing: boolean;
  /* eslint-enable @typescript-eslint/naming-convention */

  //tracked and calculated

  public contractDisplay = '';
  public contractEndDate = '';
  public display = '';
  public displaySecondary = '';
  public customerDescription = ''; //note that this is different than the `CustomerDescription` property of the same name but different casing!
  public headerConfig: ITableConfigEntity<keyof this>[] = [];
  public shouldShow: boolean;
  public canDelete = false;
  public isForeign: boolean; //is true when the timesheet owner is different from the logged in user.
  public __config: ITableConfigEntity<keyof this>[] = [];
  public contractDetails?: ContractHandler = undefined;

  private isDeleteTriggered = false;
  private $timer?: Subscription = undefined;
  /**@deprecated  We want to stop using this fake boolean property and instead use the type property, since timesheets can only be one type.*/
  // eslint-disable-next-line @typescript-eslint/prefer-readonly -- This property is not read only, its set outside of typescript's understanding.
  private Nonbillable?: SapBooleanEnum;

  constructor(
    sapData: IExtDocument,
    private readonly datahub: DatahubService,
    private readonly searchForUpdates: boolean,
    serviceOrder?: ContractHandler,
  ) {
    this.init(sapData, serviceOrder);
  }

  public isBillable(): boolean {
    //this method identifies if the timesheet is billable. Allows us to handle all checks for nonbill to include per diems, and to easily change the definition.
    return !this.isNonBill() && !this.isPerDiem();
  }

  public isNonBill(): boolean {
    return this.Type === TimesheetTypeEnum.Nonbillable || this.Nonbillable === SapBooleanEnum.True;
  }

  public isPerDiem(): boolean {
    return this.Type === TimesheetTypeEnum.PerDiem;
  }

  private init(sapData: IExtDocument, serviceOrder?: ContractHandler): void {
    syncObjectProperties(sapData, this, false);
    if (this.Printing) this.handlePrintingCase();
    this.display = `#${trimLeadingZeros(this.Documentno)} ${this.Description}`;
    this.canDelete = this.Status === TimesheetStatusEnum.Created && this.isBillable();
    this.isForeign = !safeCompare(this.Owner, this.datahub.user.getApprover());
    if (serviceOrder) this.contractDetails = serviceOrder;
    if (this.isBillable()) {
      this.datahub.contracts.ready$.subscribe({
        next: (isReady) => {
          if (isReady) this.findServiceOrder();
        },
        error: (e) => {
          console.error('Error finding service order:', e);
        },
      });
    } else {
      this.setNonbillDescription();
    }
  }

  private handlePrintingCase(): void {
    if (this.searchForUpdates) this.setRefreshSelfTimer();
  }

  private setRefreshSelfTimer(): void {
    this.$timer = interval(5000)
      .pipe(
        take(50),
        switchMap(() => this.refreshSelf$()),
      )
      .subscribe({
        next: () => {
          this.killRefreshTimer();
        },
        error: (err) => {
          console.error('Error refreshing timesheet:', err);
        },
      });
  }

  public killRefreshTimer(): void {
    if (this.$timer == null) return;
    this.$timer.unsubscribe();
  }

  private refreshSelf$(): Observable<IExtDocument | undefined> {
    return this.datahub.apiService.getTimesheets$({ Documentno: this.Documentno }).pipe(
      map((docsArr) => docsArr[0]),
      filter((doc) => !doc?.Printing),
      tap((doc) => {
        if (doc != null && !doc.Printing) this.init(doc, this.contractDetails);
      }),
    );
  }

  private findServiceOrder(): void {
    this.contractDetails = this.datahub.contracts.findContractByServiceOrder(this.Salesdoc);
    if (this.contractDetails) {
      this.headerConfig = this.contractDetails.getHeaderConfig();
      this.setDisplayTextProps();
      this.setCustomerDescription();
    } else {
      this.datahub.contracts.getNewServiceOrder$(this.Salesdoc, this.datahub.weDate.getFilterString()).subscribe({
        next: (contract) => {
          if (!contract) {
            this.datahub.messageService.error(
              'This contract does not have a valid service order. The Contract Must have a valid service order to be billed properly. Contact Daveytime Admin.',
            );
            throw Error('You attempted to look for a contract without supplying a valid service order number.');
          }
          this.contractDetails = contract;
          this.headerConfig = this.contractDetails.getHeaderConfig();
          this.setDisplayTextProps();
          this.setCustomerDescription();
        },
        error: (err) => {
          console.error('Error finding service order:', err);
        },
      });
    }
  }

  private setNonbillDescription(): void {
    this.display = this.Description;
  }

  private setCustomerDescription(): void {
    if (!this.contractDetails) return;
    this.contractDetails.__skfsReady$.subscribe({
      next: () => {
        this.customerDescription = this.contractDetails?.value.CustomerName ?? '';
      },
      error: (err) => {
        console.error('Error setting customer description:', err);
      },
    });
  }

  public makeForeignConfig(config: ITableConfigEntity[]): ITableConfigEntity[] {
    if (this.isForeign) {
      return config.map((item) => {
        const configEntity = structuredClone(item);
        if (configEntity.icon === 'info') return configEntity;
        configEntity.isReadOnly = true;
        configEntity.tooltip = 'This timesheet not owned by you. You cannot approve or edit this time.';
        configEntity.isDisabled = true;
        return configEntity;
      });
    } else {
      return config;
    }
  }

  private setDisplayTextProps(): void {
    this.contractDisplay = this.contractDetails?.display ?? ''; //Note that the `display` property here will be a combination of the contract name and the contract date range
    this.contractEndDate = this.contractDetails?.expirationDateFriendly ?? '';
    this.display = this.Description;
    this.displaySecondary = [
      this.Genericfld01,
      this.Genericfld02,
      this.Genericfld03,
      this.Genericfld04,
      this.Genericfld05,
      this.Genericfld06,
      this.Genericfld07,
      this.Genericfld08,
      this.Genericfld09,
      this.Genericfld10,
    ]
      .filter((item) => item.trim() !== '') //remove blank/empty items
      .join(' ~ ') //combine all items together as a string
      .trim(); //then remove any trailing/leading spaces
  }

  public getServiceOrder(): string {
    return this.Salesdoc;
  }

  private runTimesheetBuilder(status: TimesheetStatusEnum.PayrollApproved | TimesheetStatusEnum.BillingApproved): void {
    this.Printing = true;
    this.Link = '';
    this.Embed = '';
    this.datahub.apiService.buildTimesheet$(this.Documentno, status).subscribe({
      next: (response) => {
        console.info('Timesheet built successfully:', response);
      },
    });
  }

  public updateSelf(status?: TimesheetStatusEnum): Subscription | null {
    if (this.isDeleteTriggered) return null;
    if (status) this.Status = status;
    if (status === TimesheetStatusEnum.BillingApproved && this.isBillable()) this.runTimesheetBuilder(status);
    if (status === TimesheetStatusEnum.PayrollApproved && !this.isBillable()) this.runTimesheetBuilder(status);
    return this.datahub.apiService.updateExtDocument$(this.getSapObject()).subscribe({
      next: () => {
        // Do nothing
      },
      error: (err) => {
        console.error('Error updating timesheet:', err);
      },
    });
  }

  public deleteSelf$(): Observable<string | undefined> {
    if (this.Status === TimesheetStatusEnum.Created) {
      this.isDeleteTriggered = true;
      return this.datahub.apiService.deleteExtDocument$(this.Documentno).pipe(map(() => this.Documentno));
    }
    return of(undefined);
  }

  public modifyConfig(baseConfig: ITableConfigEntity<keyof this>[]): void {
    this.__config = structuredClone(baseConfig);
    const owner = this.__config.find((x) => x.key === 'Owner');
    const canPrint = !!this.Link;
    if (owner && this.isBillable()) {
      owner.selectOptions = this.datahub.approvers.getSelectOptionsByCostCenter(this.Costcenter1);
    }

    if (!this.canDelete && canPrint) {
      return;
    }

    if (!canPrint) {
      this.handleConfigNoPrinting();
    }

    if (this.canDelete && this.isBillable()) {
      this.handleConfigCanDelete();
    }
  }

  private getSapObject(): Partial<IExtDocument> {
    return {
      Owner: this.Owner,
      Description: this.Description,
      Status: this.Status,
      Autocreate: this.Autocreate ? SapBooleanEnum.True : SapBooleanEnum.False,
      Dateentered: this.Dateentered,
      Documentno: this.Documentno,
      Pernr: this.Pernr,
      Timeentered: this.Timeentered,
      Salesdoc: this.Salesdoc,
      Wbs: this.Wbs,
      CustGroup: this.CustGroup,
      CondGroup: this.CondGroup,
      Costcenter1: this.Costcenter1,
      Costcenter2: this.Costcenter2,
      Genericfld01: String(this.Genericfld01),
      Genericfld02: String(this.Genericfld02),
      Genericfld03: String(this.Genericfld03),
      Genericfld04: String(this.Genericfld04),
      Genericfld05: String(this.Genericfld05),
      Genericfld06: String(this.Genericfld06),
      Genericfld07: String(this.Genericfld07),
      Genericfld08: String(this.Genericfld08),
      Genericfld09: String(this.Genericfld09),
      Genericfld10: String(this.Genericfld10),
      Nonbillable: this.Nonbillable,
      ContractDescription: this.ContractDescription,
      Printing: this.Printing,
    };
  }

  private handleConfigNoPrinting(): void {
    const found = this.__config.find((x) => x.key === 'Link');
    if (!found) {
      throw Error('Failed attempt to set extDoc config due to base passed config lacking property "Link"');
    }
    found.shouldDetectClick = false;
    found.tooltip = 'Either this timesheet is not approved or the output is not ready yet.';
    if (this.Printing) {
      found.type = 'spinner';
    }
  }

  private handleConfigCanDelete(): void {
    const found = this.__config.find((x) => x.key === 'canDelete');
    if (!found) {
      throw Error('Failed attempt to set extDoc config due to base passed config lacking property "canDelete"');
    }
    found.shouldDetectClick = true;
    found.tooltip = '';
  }
}
